"use client";

import React, { useEffect, useRef, useState } from "react";
import { ShieldAlert, House, Hammer, CircleCheck, Share2, CircleAlert } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button, buttonVariants } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Link } from "@/components/common/Link";
import { cn } from "@/lib/utils";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const pillars = [{
  id: "facade",
  icon: House,
  title: "Façade Restoration",
  description: "Specialised exterior refurbishment and protective coating systems designed to restore building aesthetics and ensure long-term structural protection."
}, {
  id: "structural",
  icon: Hammer,
  title: "Structural Remediation",
  description: "Precision solutions for concrete cancer, reinforcement corrosion, and load-bearing upgrades to extend asset life and maintain building performance."
}, {
  id: "compliance",
  icon: CircleCheck,
  title: "Defect Management",
  description: "Comprehensive identification, reporting, and rectification of building defects using transparent project management software and expert inspections."
}];
export default function SafetyInEveryMile() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll<HTMLElement>({
    threshold: 0.1
  });
  return <section data-section-id="224778" id="asset-performance-compliance" ref={sectionRef} className="relative w-full overflow-hidden dark bg-background py-24 text-foreground">
            {}
            <div className="absolute inset-0 -z-10 h-full w-full bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] opacity-10 [mask-image:radial-gradient(ellipse_80%_80%_at_50%_50%,#000_20%,transparent_100%)]"></div>

            <div className="container mx-auto px-4 md:px-6">
                <div className="mb-16 max-w-3xl">
                    <h2 className={`font-default text-3xl font-bold uppercase tracking-widest text-foreground sm:text-4xl md:text-5xl transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        Asset Performance & Compliance
                    </h2>
                    <p className={`mt-4 text-lg font-default text-muted-foreground transition-all duration-700 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                        Bottom Up Projects ensures structural integrity and building longevity through specialised remedial works and rigorous defect management protocols across all industrial sectors.
                    </p>
                </div>

                <div className="grid gap-12 lg:grid-cols-2 lg:items-center">
                    {}
                    <div className="flex flex-col gap-6">
                        {pillars.map((pillar, i) => {
            const Icon = pillar.icon;
            return <Card key={pillar.id} data-index={i} className={`group relative overflow-hidden rounded-sm border-border bg-card/40 backdrop-blur-sm transition-all duration-500 hover:bg-card/60 hover:shadow-lg ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`} style={{
              transitionDelay: `${200 + i * 150}ms`
            }}>
                                    <div className="absolute inset-0 bg-gradient-to-r from-primary/5 to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                                    <CardHeader className="flex flex-row items-start gap-4 pb-2">
                                        <div className="flex size-12 shrink-0 items-center justify-center rounded-sm bg-primary/10 text-primary ring-1 ring-primary/30 transition-transform duration-300 group-hover:scale-105">
                                            <Icon className="size-6" />
                                        </div>
                                        <div className="space-y-1">
                                            <CardTitle className="font-default text-xl font-bold tracking-tight">{pillar.title}</CardTitle>
                                            <CardDescription className="font-default text-base leading-relaxed text-muted-foreground">
                                                {pillar.description}
                                            </CardDescription>
                                        </div>
                                    </CardHeader>
                                </Card>;
          })}
                    </div>

                    {}
                    <div className={`relative aspect-square w-full max-w-md mx-auto lg:max-w-none lg:aspect-[4/3] rounded-sm border border-border bg-secondary/10 p-4 shadow-2xl backdrop-blur-md transition-all duration-1000 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`} style={{
          transitionDelay: '400ms'
        }}>
                        {}
                        <div className="relative h-full w-full overflow-hidden rounded-sm bg-background border border-border/50 shadow-inner">
                            {}
                            <svg className="absolute inset-0 h-full w-full opacity-10" viewBox="0 0 400 300" preserveAspectRatio="none">
                                <path d="M-50,50 Q100,100 200,50 T450,150" fill="none" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" />
                                <path d="M50,-50 Q150,150 50,350" fill="none" stroke="currentColor" strokeWidth="1" />
                                <path d="M350,-50 Q250,150 350,350" fill="none" stroke="currentColor" strokeWidth="1" />
                                <path d="M-50,250 Q150,200 450,280" fill="none" stroke="currentColor" strokeWidth="1" />
                            </svg>

                            {}
                            <svg className="absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
                                <path id="route-path" d="M 10 90 Q 40 50 90 10" fill="none" stroke="var(--color-primary)" strokeWidth="1" strokeLinecap="square" className="opacity-40" />
                            </svg>

                            {}
                            <div className="absolute bottom-[10%] left-[10%] size-4 -translate-x-1/2 translate-y-1/2 border border-primary bg-background shadow-sm" />
                            <div className="absolute top-[10%] right-[10%] size-5 -translate-x-1/2 -translate-y-1/2 bg-primary flex items-center justify-center">
                                <div className="size-2 bg-background" />
                            </div>

                            {}
                            <div className="absolute inset-0 h-full w-full">
                                <style dangerouslySetInnerHTML={{
                __html: `
                                    @keyframes moveAlongPath {
                                        0% { left: 10%; bottom: 10%; transform: translate(-50%, 50%) scale(1); }
                                        50% { left: 45%; bottom: 55%; transform: translate(-50%, 50%) scale(1.05); }
                                        100% { left: 90%; bottom: 90%; transform: translate(-50%, 50%) scale(1); }
                                    }
                                    @keyframes pulseRing {
                                        0% { transform: scale(0.8); opacity: 0.4; }
                                        100% { transform: scale(2.2); opacity: 0; }
                                    }
                                    @keyframes popIn {
                                        0%, 20%, 100% { opacity: 0; transform: translateY(10px) scale(0.95); pointer-events: none; }
                                        30%, 90% { opacity: 1; transform: translateY(0) scale(1); pointer-events: auto; }
                                    }
                                    @keyframes popInDelayed {
                                        0%, 60%, 100% { opacity: 0; transform: translateY(10px) scale(0.95); pointer-events: none; }
                                        70%, 95% { opacity: 1; transform: translateY(0) scale(1); pointer-events: auto; }
                                    }
                                    .animate-vehicle {
                                        animation: moveAlongPath 8s ease-in-out infinite alternate;
                                    }
                                    .animate-pulse-ring {
                                        animation: pulseRing 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
                                    }
                                    .animate-tooltip-1 {
                                        animation: popIn 8s ease-in-out infinite alternate;
                                    }
                                    .animate-tooltip-2 {
                                        animation: popInDelayed 8s ease-in-out infinite alternate;
                                    }
                                `
              }} />

                                {}
                                <div className="animate-vehicle absolute z-20 flex flex-col items-center">
                                    {}
                                    <div className="animate-tooltip-1 absolute bottom-full mb-3 flex w-max items-center gap-1.5 rounded-sm bg-primary px-3 py-1.5 font-mono text-[10px] uppercase font-bold text-primary-foreground shadow-lg">
                                        <Share2 className="size-3" />
                                        Defect Logged
                                        <div className="absolute -bottom-1 left-1/2 size-2 -translate-x-1/2 rotate-45 bg-primary" />
                                    </div>

                                    {}
                                    <div className="animate-tooltip-2 absolute top-full mt-3 flex w-max items-center gap-1.5 rounded-sm bg-destructive px-3 py-1.5 font-mono text-[10px] uppercase font-bold text-destructive-foreground shadow-lg">
                                        <CircleAlert className="size-3" />
                                        Risk Alert
                                        <div className="absolute -top-1 left-1/2 size-2 -translate-x-1/2 rotate-45 bg-destructive" />
                                    </div>

                                    {}
                                    <div className="relative flex size-6 items-center justify-center">
                                        <div className="animate-pulse-ring absolute inset-0 bg-primary" />
                                        <div className="relative z-10 size-3 bg-primary shadow-sm ring-1 ring-background" />
                                    </div>
                                </div>
                            </div>

                            {}
                            <div className="absolute top-4 left-4 flex items-center gap-2 rounded-sm bg-background/90 px-3 py-1.5 font-mono text-[10px] uppercase font-bold text-foreground shadow-sm backdrop-blur-md border border-border">
                                <div className="size-2 animate-pulse rounded-lg bg-primary" />
                                Compliance Sync
                            </div>
                            
                            <div className="absolute bottom-4 right-4">
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Link to="https://set-starfish.10web.cloud/contact/" className={cn(buttonVariants({
                      variant: "destructive",
                      size: "icon"
                    }), "size-10 rounded-sm shadow-lg hover:scale-105 transition-transform")}>
                                                <ShieldAlert className="size-5" />
                                                <span className="sr-only">Urgent Repair Request</span>
                                            </Link>
                                        </TooltipTrigger>
                                        <TooltipContent side="left" className="font-mono text-xs uppercase">
                                            Request Urgent Inspection
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>;
}